import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { oauth2 as SMART } from 'fhirclient';
import Client from 'fhirclient/lib/Client';
import { jwtDecode } from 'jwt-decode';
import {
  deleteSession,
  readSessionObject,
  writeSessionObject,
} from '../storages/session-storage';
import { captureLog } from '../lib/error-handler';

const sessionKey = 'ehr-session';
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const FALLBACK_TOKEN_LIFETIME_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Returns the number of milliseconds to wait before pinging the FHIR server to
 * trigger a token refresh. Calculated as 5 minutes before the token's expiry
 * time, derived from the `iat` and `exp` claims in the JWT access token.
 * Falls back to {@link FALLBACK_TOKEN_LIFETIME_MS} minus
 * {@link REFRESH_BEFORE_EXPIRY_MS} if the token doesn't include expiry info.
 */
function getRefreshDelayMs(client: Client): number {
  const accessToken = client.getState('tokenResponse.access_token') as
    | string
    | undefined;
  let jwtPayload: { exp?: number } | null = null;
  try {
    jwtPayload = accessToken ? jwtDecode<{ exp?: number }>(accessToken) : null;
  } catch {
    // Access token is not a valid JWT (e.g. opaque token) — fall through to
    // the fallback delay below.
  }

  const exp = jwtPayload?.exp ?? null;

  if (exp !== null) {
    // exp is a Unix timestamp in seconds, so multiply by 1000 to convert to
    // milliseconds.
    return Math.max(exp * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY_MS, 0);
  }

  // Fallback if the token doesn't include expiry information
  return FALLBACK_TOKEN_LIFETIME_MS - REFRESH_BEFORE_EXPIRY_MS;
}

/**
 * All the fields are optional because we may not get all the fields from the
 * EHR. We have validation in place to make sure we don't continue without the
 * required fields.
 *
 * There are also other fields we get back from the EHR, we just don't need them
 * yet, so not adding them here.
 */
export interface TokenResponse {
  encounter?: string;
  patient?: string;
  patientDOB?: string;
  patientFirstName?: string;
  patientLastName?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
}

export interface EhrSession {
  fhirAppId: number | null;
  fhirAppClientId: string;
  testSession: boolean;
  ehrAccessToken: TokenResponse | null;
  ehrSessionId: number | null;
  ehrRiderId: string | null;
}

export function saveEhrSession(session: EhrSession) {
  writeSessionObject(sessionKey, session);
}

export function readEhrSession() {
  return readSessionObject(sessionKey) as EhrSession | null;
}

export function deleteEhrSession() {
  return deleteSession(sessionKey);
}

export const FhirClientContext = createContext<
  | {
      client: Client | null;
      error: any | null;
      ehrSession: EhrSession | null;
      setEhrSession: React.Dispatch<React.SetStateAction<EhrSession | null>>;
    }
  | undefined
>(undefined);

export default function FhirClientProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState<any | null>(null);
  const [ehrSession, setEhrSession] = useState<EhrSession | null>(null);

  useEffect(() => {
    if (SMART === undefined) {
      return;
    }

    SMART.ready().then(
      client => {
        const hasRefreshToken = !!client.getState(
          'tokenResponse.refresh_token',
        );

        // TODO: I want to log the presence of a refresh token so we can
        // understand how many of our users are able to have their sessions
        // refreshed automatically vs how many will need to manually
        // re-authenticate after token expiry. We can remove this after a while.
        if (hasRefreshToken) {
          captureLog('info', '[Epic EHR] - FHIR client ready with refresh token');
        } else {
          captureLog('warn', '[Epic EHR] - FHIR client ready without a refresh token — Epic session cannot be refreshed after token expiry');
        }

        setClient(client);
      },
      error => {
        setError(error);
      },
    );
  }, []);

  // Ping the FHIR server 5 minutes before the Epic access token expires so
  // fhirclient can refresh it. Requires Epic to have issued a refresh token
  // (via online_access or offline_access scope); has no effect otherwise.
  useEffect(() => {
    if (!client) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const scheduleRefresh = () => {
      const delayMs = getRefreshDelayMs(client);
      timeoutId = setTimeout(() => {
        client
          .request('metadata')
          .then(() => {
            if (!cancelled) scheduleRefresh();
          })
          .catch(() => {
            // Silently ignore — the token has expired and there is no refresh
            // token available. The user will see an error when they next try to
            // write data back to the EHR.
          });
      }, delayMs);
    };

    scheduleRefresh();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [client]);

  return (
    <FhirClientContext.Provider
      value={{ client, error, ehrSession, setEhrSession }}
    >
      {children}
    </FhirClientContext.Provider>
  );
}

export function useEhrContext() {
  const context = useContext(FhirClientContext);

  if (context === undefined) {
    throw new Error('useEhrContext must be used within a FhirClientContext');
  }

  const { client, error, ehrSession, setEhrSession } = context;
  const [isEhr, setIsEhr] = useState<boolean>(false);
  const [isEhrPatientContext, setIsEhrPatientContext] =
    useState<boolean>(false);

  const ehrToken = useMemo(() => {
    if (!client) {
      return null;
    }

    const token = client.getState('tokenResponse');
    return token ? (token as TokenResponse) : null;
  }, [client]);

  useEffect(() => {
    if (!client) {
      return;
    }

    setIsEhr(true);

    const patientId = client.getState('tokenResponse.patient');

    if (patientId) {
      setIsEhrPatientContext(true);
    }
  }, [client]);

  useEffect(() => {
    if (ehrSession === null) {
      const session = readEhrSession();

      if (session) {
        setEhrSession(session);
      }
    }
  }, [ehrSession, setEhrSession]);

  return {
    ehrSession: ehrSession || null,
    setEhrSession,
    ehrToken,
    fhirClient: client,
    fhirError: error,
    isEhr,
    isEhrPatientContext,
  };
}
